import { type ElementType, type FC, useState } from 'react';

import {
  Avatar,
  Box,
  type ComponentWithAs,
  Flex,
  HStack,
  IconButton,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { LuChevronsRight } from 'react-icons/lu';
import { useLocation } from 'react-router-dom';

import {
  IconChartPieFill,
  IconMonitorPlayFill,
  type IconProps,
  IconSettingsFill,
  IconStoreFill,
  IconUsersFill,
  Link,
} from '@/components';
import { getFullname } from '@/utils/identity';
import { variantPath } from '@/utils/variant/variantPath';

import { useGetAccountQuery } from '../auth';
import { LinkAdmin } from './LinkAdmin';
import { ProductSwitcher } from './ProductSwitcher';
import { ADMIN_MAIN_HEADER_HEIGHT } from './constants';
import { type TMainSidebarItem } from './types';

const MotionFlex = motion(Flex);

type MainSidebarProps = {
  isCollapsed: boolean;
  setIsCollapsed: (isCollapsed: boolean) => void;
};

export const MainSidebar: FC<MainSidebarProps> = ({
  isCollapsed,
  setIsCollapsed,
}) => {
  const account = useGetAccountQuery();
  const { t } = useTranslation(['components']);

  const [showText, setShowText] = useState(true);

  const toggleSidebar = () => {
    setIsCollapsed(!isCollapsed);
  };

  const handleAnimationComplete = () => {
    if (isCollapsed) {
      setShowText(false);
    } else {
      setTimeout(() => {
        setShowText(true);
      }, 300);
    }
  };

  const ATSRoutes_SidebarItems: Array<TMainSidebarItem> = [
    {
      text: t('components:layout.controlPanel'),
      icon: IconStoreFill,
      linkTo: '/admin/boards',
    },
    {
      text: t('components:layout.positions'),
      icon: IconMonitorPlayFill,
      linkTo: '/admin/boards/positions',
    },
    {
      text: t('components:layout.candidates'),
      icon: IconUsersFill,
      linkTo: '/admin/boards/candidates',
    },
    // {
    //   text: t('components:layout.inbox'),
    //   icon: IconMonitorPlay,
    //   linkTo: '/admin/boards',
    // },
    // {
    //   text: t('components:layout.calendar'),
    //   icon: IconMonitorPlay,
    //   linkTo: '/admin/boards',
    // },
    {
      text: t('components:layout.reports'),
      icon: IconChartPieFill,
      linkTo: '/admin/boards/reports',
    },
    // {
    //   text: t('components:layout.notifications'),
    //   icon: IconMonitorPlay,
    //   linkTo: '/admin/boards',
    // },
  ];

  // const ATSRoutes_SidebarItems: Array<TMainSidebarItem> = [
  //   {
  //     text: t('components:layout.positions'),
  //     icon: IconMonitorPlayFill,
  //     linkTo: '/admin/boards',
  //   },
  //   {
  //     text: t('components:layout.candidates'),
  //     icon: IconUsersFill,
  //     linkTo: '/admin/boards/candidates',
  //   },
  //   {
  //     text: t('components:layout.reports'),
  //     icon: IconChartPieFill,
  //     linkTo: '/admin/boards/reports',
  //   },
  // ];

  const fullName = getFullname(account.data?.name, account.data?.surname);

  return (
    <MotionFlex
      h="100vh"
      flex={1}
      bg="white"
      borderRight="1px solid"
      borderBottom="1px solid"
      borderColor="gray.100"
      initial={{ width: '300px' }}
      transition={{ duration: 0.3 }}
      onAnimationStart={handleAnimationComplete}
      animate={{ width: isCollapsed ? '80px' : '300px' }}
    >
      <VStack justify="space-between" w="full">
        <VStack pb={4} gap={8} w="full">
          <HStack
            justify={showText ? 'space-between' : 'center'}
            align="center"
            w="full"
            h={ADMIN_MAIN_HEADER_HEIGHT}
            gap={3}
            px={4}
            borderBottom="1px solid"
            borderColor="gray.100"
          >
            {showText && (
              <Flex
                align="center"
                gap={2}
                flex={1}
                as={LinkAdmin}
                to="/dashboard"
              >
                <Flex
                  borderRadius="md"
                  border="1px solid"
                  borderColor="gray.100"
                  bg="white"
                  p={0}
                >
                  {variantPath('LogoIconWhite')}
                </Flex>
                <Text fontWeight="bold" fontSize="20px">
                  {variantPath('WebsiteName')}
                </Text>
              </Flex>
            )}
            <IconButton
              aria-label="Toggle sidebar"
              color="gray.600"
              variant="unstyled"
              size="sm"
              _hover={{ color: 'gray.800' }}
              onClick={toggleSidebar}
              h={10}
              shadow="none"
            >
              <motion.div
                animate={{ rotate: isCollapsed ? 0 : 180 }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
              >
                <LuChevronsRight fontSize="24px" />
              </motion.div>
            </IconButton>
          </HStack>

          <VStack gap={4} w="full" px={4}>
            <Box w="full">
              <ProductSwitcher isCollapsed={!showText} />
            </Box>

            <VStack gap={3} w="full">
              {ATSRoutes_SidebarItems.map((item) => {
                return (
                  <Item
                    key={item.text}
                    icon={item.icon}
                    text={item.text}
                    showText={showText}
                    isCollapsed={isCollapsed}
                    linkTo={item.linkTo}
                  />
                );
              })}
            </VStack>
          </VStack>
        </VStack>

        <VStack gap={3} w="full">
          <Flex flex={1} w="full" px={4}>
            <Item
              text={t('components:layout.settings')}
              icon={IconSettingsFill}
              linkTo="/admin/boards/settings"
              showText={showText}
              isCollapsed={isCollapsed}
            />
          </Flex>
          <HStack
            py={4}
            px={5}
            gap={3}
            w="full"
            color="brand.100"
            _hover={{ color: 'brand.50' }}
            transition="0.2s"
            borderColor="gray.100"
            borderTopWidth={1}
          >
            <Avatar
              bg="white"
              name={account.isLoading ? undefined : fullName}
              fontWeight="bold"
              size="sm"
              width={9}
              height={9}
              borderWidth={1}
              borderColor="gray.100"
            />
            <Stack spacing={0}>
              <Text
                fontWeight="semibold"
                noOfLines={1}
                color="gray.900"
                opacity={isCollapsed ? 0 : 1}
              >
                {fullName}
              </Text>
              <Text
                fontSize="xs"
                color="gray.600"
                isTruncated
                maxWidth={200}
                opacity={isCollapsed ? 0 : 1}
              >
                {account.data?.email}
              </Text>
            </Stack>
          </HStack>
        </VStack>
      </VStack>
    </MotionFlex>
  );
};

export const Item = ({
  text,
  icon,
  linkTo,
  isCollapsed,
  showText,
}: {
  text: string;
  icon: ComponentWithAs<ElementType, Omit<IconProps, 'icon'>>;
  linkTo: string;
  isCollapsed: boolean;
  showText: boolean;
}) => {
  const Icon = icon;
  const { pathname } = useLocation();
  const isActive = pathname === linkTo;

  return (
    <Link to={linkTo} w="full">
      <HStack
        py={2}
        px={3}
        gap={2}
        w="full"
        cursor="pointer"
        color={isActive ? 'gray.900' : 'gray.600'}
        bg={isActive ? 'brand.25' : 'white'}
        _hover={{
          color: 'gray.700',
          bg: isActive ? 'brand.25' : 'gray.25',
        }}
        borderWidth={isActive ? 1 : 0}
        borderColor={isActive ? 'brand.100' : 'gray.100'}
        transition="0.2s"
        borderRadius="lg"
      >
        <Icon fontSize="20px" color={isActive ? 'brand.500' : 'gray.600'} />

        {showText && (
          <Text
            fontWeight="medium"
            opacity={isCollapsed ? 0 : 1}
            transition="opacity 0.3s"
            display={showText ? 'block' : 'none'}
            noOfLines={1}
          >
            {text}
          </Text>
        )}
      </HStack>
    </Link>
  );
};
